import { Group, Button, TextInput, NumberInput, Select, Fieldset } from '@mantine/core';
import { useForm } from '@mantine/form';

import { CustomCopyBtn } from '@/shared';
import { useGetConfigBaseUrlQuery } from '@/modules/config';
import {
  partnerCommissionCalculationBases,
  partnerCommissionCalculationBaseTitles,
  shopTypes,
  shopTypeTitles,
} from '../utils/const/common';
import { getPartnerCommissions } from '../utils/helpers/partner-commissions';
import { getReferralLink } from '../utils/helpers/referral-link';

import type { TPartnerCommission, TShop, TShopUpdateParams } from '../store/types';

type TPartnerCommissionForm = Omit<TPartnerCommission, 'commissionPercent'> & {
  commissionPercent: number | string;
};

type FormData = Omit<TShopUpdateParams, 'partnerCommissions'> & {
  partnerCommissions: TPartnerCommissionForm[];
};

type Props = {
  shop: TShop;
  onSubmit: (params: TShopUpdateParams) => void;
  onClose: () => void;
};

export const EditShopForm = (props: Props) => {
  const { shop, onSubmit, onClose } = props;
  const { data: baseUrlConfig } = useGetConfigBaseUrlQuery();
  const referralLink = getReferralLink(baseUrlConfig?.baseUrl, shop.referralCode);

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: {
      shopId: shop.id,
      name: shop.name,
      callbackUrl: shop.callbackUrl,
      ownerId: shop.ownerId ?? undefined,
      homepageUrl: shop.homepageUrl,
      successUrl: shop.successUrl,
      failureUrl: shop.failureUrl,
      partnerCommissions: shop.type === 'partner'
        ? getPartnerCommissions(shop.balances.map((balance) => balance.currency), shop.partnerCommissions)
        : [],
    },
    validate: {
      shopId: (value) => !value ? 'Введите ID магазина' : null,
      name: (value) => !value ? 'Введите название магазина' : null,
      callbackUrl: (value) => !value ? 'Введите Callback URL' : null,
      homepageUrl: (value) => !value ? 'Введите URL домашней страницы' : null,
      successUrl: (value) => !value ? 'Введите URL для успешной операции' : null,
      failureUrl: (value) => !value ? 'Введите URL для неудачной операции' : null,
      partnerCommissions: {
        calculationBase: (value) => !value ? 'Выберите базу расчёта' : null,
        commissionPercent: (value) => {
          if (value === '' || Number.isNaN(Number(value))) return 'Введите процент комиссии';
          if (Number(value) < 0 || Number(value) > 100) return 'Процент должен быть от 0 до 100';

          return null;
        },
      },
    },
  });

  const handleSubmit = (values: FormData) => {
    const params: TShopUpdateParams = {
      ...values,
      ownerId: values.ownerId ? values.ownerId : undefined,
      partnerCommissions: shop.type === 'partner'
        ? values.partnerCommissions.map((commission) => ({
          ...commission,
          commissionPercent: Number(commission.commissionPercent),
        }))
        : undefined,
    };

    onSubmit(params);
    onClose();
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <NumberInput
        label="ID магазина"
        disabled
        key={form.key('shopId')}
        {...form.getInputProps('shopId')}
      />
      <TextInput
        label="Название магазина"
        withAsterisk
        key={form.key('name')}
        {...form.getInputProps('name')}
      />
      <Select
        label="Тип магазина"
        disabled
        allowDeselect={false}
        data={shopTypes.map((type) => ({ value: type, label: shopTypeTitles[type] }))}
        value={shop.type}
      />
      <NumberInput
        label="ID владельца"
        disabled={shop.type === 'partner'}
        allowDecimal={false}
        min={1}
        key={form.key('ownerId')}
        {...form.getInputProps('ownerId')}
      />
      {shop.type === 'partner' && (
        <>
          <TextInput
            label="Реферальная ссылка"
            readOnly
            value={referralLink ?? ''}
            rightSectionPointerEvents="all"
            rightSection={referralLink
              ? <CustomCopyBtn text={referralLink} showText={false} tooltipPosition="top" rightIcon />
              : null
            }
          />
          <Fieldset legend="Партнёрские комиссии" mt="md" styles={{ root: { padding: '10px' } }}>
            {shop.balances.map((balance, index) => (
              <Group key={balance.currency} grow align="flex-start">
                <NumberInput
                  label={`${balance.currency} (%)`}
                  decimalScale={10}
                  step={0.1}
                  min={0}
                  max={100}
                  key={form.key(`partnerCommissions.${index}.commissionPercent`)}
                  {...form.getInputProps(`partnerCommissions.${index}.commissionPercent`)}
                />
                <Select
                  label="База расчёта"
                  allowDeselect={false}
                  data={partnerCommissionCalculationBases.map((calculationBase) => ({
                    value: calculationBase,
                    label: partnerCommissionCalculationBaseTitles[calculationBase],
                  }))}
                  key={form.key(`partnerCommissions.${index}.calculationBase`)}
                  {...form.getInputProps(`partnerCommissions.${index}.calculationBase`)}
                />
              </Group>
            ))}
          </Fieldset>
        </>
      )}
      <TextInput
        label="Callback URL"
        withAsterisk
        key={form.key('callbackUrl')}
        {...form.getInputProps('callbackUrl')}
      />
      <TextInput
        label="URL домашней страницы"
        withAsterisk
        key={form.key('homepageUrl')}
        {...form.getInputProps('homepageUrl')}
      />
      <TextInput
        label="URL для редиректа успешного выполнения операции"
        withAsterisk
        key={form.key('successUrl')}
        {...form.getInputProps('successUrl')}
      />
      <TextInput
        label="URL для редиректа неудачного выполнения операции"
        withAsterisk
        key={form.key('failureUrl')}
        {...form.getInputProps('failureUrl')}
      />

      <Group justify="flex-end" mt="30">
        <Button variant="outline" onClick={() => onClose()}>
          Отменить
        </Button>
        <Button type="submit">
          Редактировать
        </Button>
      </Group>
    </form>
  );
};
